import React from "react";
import dayjs from "dayjs";
import {
  SelectionMarketNameMap,
  OversMarkets,
  SubHeaderDescriptionMap,
  StatsPanelMatchSelectionMessageMap,
  ResultsMarkets,
  StatsPanelSingleTeamMatchSelectionMessageMap,
} from "../constants";
import CategoryFootball from "../assets/icons/category_football.svg";
import styles from "../components/SelectionRow/SelectionRow.module.scss";

export const customSortOrder = (array, sortOrder) => {
  // This is used to sort the order of the filter selections in a specific order than what is returned from the
  // api, sortOrder is the array we are using to order the passed down array in the custom order we want
  if (array?.length) {
    return array.sort(
      (a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type)
    );
  }
  return [];
};

export const getDateFormat = (date) => {
  // This is used to return today or tomorrow's date by passing in the active date string and converting
  // into the YYYY-MM-DD format
  if (date === "tomorrow") {
    return dayjs().add(1, "day").format("YYYY-MM-DD");
  }

  if (date === "today") {
    return dayjs().format("YYYY-MM-DD");
  }

  return "";
};

export const handleDateFormat = (date) => {
  // This is used to return the passed down date in the selected format ("YYYY-MM-DD");
  // If no date is passed we return today'a date in the selected format ("YYYY-MM-DD");

  if (date) {
    return dayjs(date).format("YYYY-MM-DD");
  }

  return dayjs().format("YYYY-MM-DD");
};

export const getTeamBadge = (id) => {
  if (id) {
    return `https://cdn.checkd-dev.com/football/teams/150x150/${id}.png`;
  }

  // TODO: add placeholder url here or use assets for onError prop for the img tag
  return null;
};

export const checkForEmptyParams = (params) => {
  const parameters = { ...params };

  Object.keys(parameters).forEach((key) => {
    // Check if a param is empty/undefined or if a param is an empty object or empty array
    // we should remove the param and not allow this to be sent to the API request.
    if (
      !parameters[key] ||
      (parameters[key] &&
        (!Object.keys(parameters[key]).length || !parameters[key].length))
    ) {
      delete parameters[key];
    }
  });

  return parameters;
};

export const handleNoImage = (e) => {
  // TODO : Hide for now - placeholder maybe to come?
  e.target.onerror = null;
  e.target.style.visibility = "hidden";
  e.target.style.height = "0px";
};

// Helper function to handle the different Stats Messages we could return depending on the current type, selectionName and marketType
export const handleStatsMessaging = (selection) => {
  // Check if it is an overs/under market type
  const isOversMarket = OversMarkets.includes(selection.marketType);
  const isResultsMarket = ResultsMarkets.includes(selection.marketType);

  // Renders the Selection Market Name
  const selectionMarketName = `${SelectionMarketNameMap.get(
    selection.marketType
  )}`;

  // Render the match markets stats filter message depending on the active stats filter
  // If it is a team market such as team cards or team corners and one team returns on the stats
  // we should display "The previous form for the <<market name>> for the <<home/away>> team’s <<stats>>".
  // We check for the statsLength being 1 and we return the statsType (home or away).
  // Otherwise we display "The previous form for the <<market name>> in both team’s <<stats>>"
  const selectionMatchStatsMessage =
    selection.statsLength === 1
      ? `for the ${
          selection.statsTeamType
        } ${StatsPanelSingleTeamMatchSelectionMessageMap.get(
          selection.statsFilter
        )}`
      : StatsPanelMatchSelectionMessageMap.get(selection.statsFilter);

  // Render the player markets stats filter message depending on the active stats filter
  const selectionPlayerStatsMessage = SubHeaderDescriptionMap.get(
    selection.statsFilter
  );

  if (selection.type === "match") {
    // If it is an over/under market we should display the selectionName passed down with the match stats message
    if (isOversMarket) {
      return `'${selection.selectionName}' ${selectionMatchStatsMessage}`;
    }

    // If it is the 1x2 market and one of the teams is selected as the winner
    // we should display the selectionMarketName declared in the const above with the text "Win" with the match stats message
    if (
      !isOversMarket &&
      isResultsMarket &&
      selection.selectionType !== "draw"
    ) {
      return `'${selectionMarketName} - Win' ${selectionMatchStatsMessage}`;
    }

    // If it is the 1x2 market and draw is selected as the selection
    // we should display the selectionMarketName declared in the const above with the text "Draw" with the match stats message
    if (
      !isOversMarket &&
      isResultsMarket &&
      selection.selectionType === "draw"
    ) {
      return `'${selectionMarketName} - Draw' ${selectionMatchStatsMessage}`;
    }

    // Otherwise we should display the selectionMarketName declared in the const above with the match stats message
    return `'${selectionMarketName} - ${selection.selectionName}' ${selectionMatchStatsMessage}`;
  }

  // If it is player we should display the selectionMarketName declared in the const above with the player stats message
  if (selection.type === "player") {
    return `'${selectionMarketName} - ${selection.selectionName}' ${selectionPlayerStatsMessage}`;
  }
};

export const handleNoCategoryLogo = (event) => {
  // Fallback to local category football logo if logoURL does not exist or fails
  event.target.src = CategoryFootball;
  event.target.alt = "Category Football logo";

  // Return event so we can test what is output
  return event;
};

export const handleTeamHighlight = (
  selectionData,
  teamName,
  side,
  marketType
) => {
  // We get either the homeTeamName or awayTeamName key by mapping through the selectionData object keys and
  // matching each key value against the teamName value passed down to find either key.
  const getKeyByValue = Object.keys(selectionData).find(
    (key) => selectionData[key] === teamName
  );

  // If it is a player market, we have a side value defined and the key obtained from the getByKeyValue function
  // starts with the side value which could be "home" or "away" then we render a bold version of the teamName
  // otherwise we render the teamName in the standard font weight
  if (marketType === "player" && side && getKeyByValue.startsWith(side)) {
    return <span className={styles.selectionName}>{teamName}</span>;
  } else {
    return teamName;
  }
};

// We filter the shuffle selections to exclude any selections that have already been added to the betslip
// to prevent them from showing up in the shuffle results when requesting new selections.
export const filterOutBetslipSelections = (acca, betslipStore) => {
  return acca.filter(
    (accaSelection) =>
      !betslipStore.selections.some(
        (betslipStoreSelections) =>
          betslipStoreSelections.id === accaSelection.selection.id &&
          betslipStoreSelections.marketId === accaSelection.selection.marketId
      )
  );
};

export const isObjEmpty = (obj) => {
  for (const i in obj) return false;

  return true;
};

export function getMapPosition(map, targetKey) {
  let position = 0;
  for (const [key] of map) {
    if (key === targetKey) {
      return position;
    }
    position++;
  }
  return 0; // Not found
}
